import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ApiConfig } from '@/config/api.config';
import { useAxiosAuth } from '@/hooks/useAxiosAuth';
import { UrlHelper } from '@/utils/UrlHelper';
import { DateTimeHelper } from '@/utils/dateTimeHelper';
import { useUserActivitiesFilter } from '@/features/superAdmin/features/userActivities/hooks/useUserActivitiesFilter';
import { UserActivityListResponseDto } from '@/features/superAdmin/features/userActivities/types/superAdmin-userActivities-dto';

export const useUserActivitiesListApi = () => {
    const axiosInstance = useAxiosAuth();
    const { filter } = useUserActivitiesFilter();
    const {
        startDate,
        endDate,
        take,
        skip,
        orderBy,
        orderDirection,
        source,
        userId,
        tenantId,
        searchText,
    } = filter;

    const {
        data: userActivitiesListData,
        isLoading: isLoadingUserActivitiesListData,
        isError: isErrorUserActivitiesListData,
    } = useQuery({
        queryKey: [
            ApiConfig.queries.superAdmin.userActivities.queryKeys.list,
            startDate,
            endDate,
            take,
            skip,
            orderBy,
            orderDirection,
            source,
            userId,
            tenantId,
            searchText,
        ],
        queryFn: async (): Promise<UserActivityListResponseDto> => {
            const url = UrlHelper.buildUrlWithQueryParams(
                ApiConfig.queries.superAdmin.userActivities.endpoints.list,
                {
                    startDate: DateTimeHelper.getDateOnly(startDate),
                    endDate: DateTimeHelper.getDateOnly(endDate),
                    take,
                    skip,
                    orderBy,
                    orderDirection,
                    source,
                    userId,
                    tenantId,
                    searchText,
                },
            );
            const response = await axiosInstance.get<UserActivityListResponseDto>(url);
            return response.data;
        },
        placeholderData: keepPreviousData,
    });

    return {
        userActivitiesListData,
        isLoadingUserActivitiesListData,
        isErrorUserActivitiesListData,
    };
};
